import { inject, Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ConfirmationModalStoreService
} from '@shared/modals/confirmation-modal/shared/confirmation-modal-store.service';
import { GuestCheckModalStoreService } from '@shared/modals/guest-check-modal/shared/guest-check-modal-store.service';
import { ToastType } from '@shared/models/toast-type';
import { AuthModalService } from '@shared/services/auth-modal.service';
import { FormCallbackService } from '@shared/services/form-callback.service';
import { BaseStoreService } from '@shared/stores/base-store.service';
import { ToastStoreService } from '@shared/stores/toast-store.service';
import { UserStoreService } from '@shared/stores/user-store.service';
import { filter, switchMap } from 'rxjs';
import { CallbackModalComponent } from '../callback-modal.component';
import { CallbackData } from './callback-data';

declare var gtag;

const GTAG_SHOW_EVENT_NAME = 'callback_modal';

@Injectable({
  providedIn: 'root'
})
export class CallbackModalStoreService extends BaseStoreService<CallbackData> {
  private authModal = inject(AuthModalService);
  private confirmationModal = inject(ConfirmationModalStoreService);
  private formCallbackService = inject(FormCallbackService);
  private guestCheckModal = inject(GuestCheckModalStoreService);
  private ngbModal = inject(NgbModal);
  private toast = inject(ToastStoreService);
  private userStore = inject(UserStoreService);

  constructor() {
    super();
  }

  show(data: CallbackData): void {
    this.current = data;

    if (!this.userStore.currentS()) {
      return this.proceedAsGuest();
    }

    this.userStore.isFastOrderAvailable()
      ? this.proceedAsAuthorized()
      : this.ngbModal.open(CallbackModalComponent)
  }

  private proceedAsAuthorized(): void {
    const { type, subject, brand_id, series_id, model_id, offer_id, color, sub_color } = this.currentS();

    this.confirmationModal.show({
      title: $localize`Увага`,
      message: $localize`Ви впевнені, що хочете залишити заявку?`,
    })
      .closed
      .pipe(
        filter(Boolean),
        switchMap(() => {
          return this.formCallbackService.submit({
            type,
            subject,
            ...(brand_id && { brand_id }),
            ...(series_id && { series_id }),
            ...(model_id && { model_id }),
            ...(offer_id && { offer_id }),
            ...(color && { color }),
            ...(sub_color && { sub_color }),
            preferred_office: this.userStore.currentS().preferred_office,
          });
        }),
      )
      .subscribe({
        next: () => {
          const values = [
            ...(offer_id ? [ `offer_${offer_id}` ] : []),
            ...(model_id ? [ `model_${model_id}` ] : []),
            ...(series_id ? [ `series_${series_id}` ] : []),
            ...(brand_id ? [ `brand_${brand_id}` ] : []),
          ];

          try {
            gtag('event', type, {
              'value': !!values.length ? values.join(',') : 'none',
            });
          } catch (e) {
            console.error('gtag not found');
          }

          this.toast.show({
            text: $localize`Заявку успішно надіслано. Наші менеджери незабаром зв'яжуться з вами`,
            type: ToastType.Success
          });
        },
        error: () => this.toast.show({
          text: $localize`Помилка! Перевірте ще раз форму або спробуйте пізніше`,
          type: ToastType.Error
        }),
      });
  }

  private proceedAsGuest(): void {
    this.guestCheckModal.show({
      title: $localize`Продовжити як гість?`,
      message: $localize`Після авторизації ви зможете оформлювати заявки в один клік.`,
      hint: $localize`Відкриється доступ до історії заявок, обраного, відгуків на авто та інших функцій, які незабаром з’являться на платформі.`,
    })
      .closed
      .pipe(
        filter(Boolean),
      )
      .subscribe((result: 'signIn' | 'guest') => {

        switch (result) {

          case 'signIn':
            this.authModal.show('signIn');
            break;

          case 'guest':
            this.ngbModal.open(CallbackModalComponent);
            break;

        }

      });
  }
}
